import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { generateClient } from 'aws-amplify/api';
import { getCloudFrontImageUrl } from '../../util/cloudFrontUtils';

import { listChatRoomsByHost, listChatRoomsByGuest } from '../../graphql/messaging';
import { onSendMessageSecure } from '../../graphql/messaging';
import { getExactLinkedBookingId } from '../../util/bookingLinking';

const amplifyClient = generateClient();

/**
 * Custom hook to fetch user chat rooms
 * @returns {Object} Chat room data and operations
 */
export const useChatRooms = (userId) => {
  const [chatRooms, setChatRooms] = useState([]);
  
  // Use new backend queries: listChatRoomsByHost and listChatRoomsByGuest
  const { data: hostData, loading: hostLoading, error: hostError, refetch: refetchHost } = useQuery(listChatRoomsByHost, {
    variables: { hostID: userId, limit: 100 },
    fetchPolicy: 'cache-and-network',
    skip: !userId,
    errorPolicy: 'all',
    context: { skipGlobalError: true },
  });

  const { data: guestData, loading: guestLoading, error: guestError, refetch: refetchGuest } = useQuery(listChatRoomsByGuest, {
    variables: { guestID: userId, limit: 100 },
    fetchPolicy: 'cache-and-network',
    skip: !userId,
    errorPolicy: 'all',
    context: { skipGlobalError: true },
  });

  const loading = hostLoading || guestLoading;
  const error = hostError || guestError;
  
  const refetch = useCallback(() => {
    refetchHost();
    refetchGuest();
  }, [refetchHost, refetchGuest]);

  // Subscribe to new messages using AWS Amplify client (handles WebSocket automatically)
  // NOTE: This subscription is optional - if it fails, the app still works
  // The per-chat-room subscriptions in useMessagesSubscription are more important
  const subscriptionRef = useRef(null);
  
  const subscribeToNewMessages = useCallback(() => {
    // Don't subscribe if userId is not available
    if (!userId) {
      return () => {}; // Return empty unsubscribe function
    }
    
    // Use AWS Amplify client for subscriptions (handles WebSocket automatically)
    // Apollo Client's subscribeToMore tries to use HTTP which doesn't work with AppSync
    try {
      const subscription = amplifyClient.graphql({
        query: onSendMessageSecure,
        variables: {} // No filter = all messages for this user
      });

      if (subscription && 'subscribe' in subscription) {
        subscriptionRef.current = subscription.subscribe({
          next: ({ data }) => {
            const newMessage = data?.onSendMessageSecure;
            if (newMessage) {
              // Trigger a refetch of chat rooms to update lastMessage
              setTimeout(() => {
                refetch();
              }, 100);
            }
          },
          error: () => {
            // Fail silently - subscription errors don't break the app
            // The chat list will still work, just won't auto-refresh on new messages
            // Don't log to avoid console spam
          }
        });
      }
    } catch (err) {
      // Fail silently - subscription setup errors don't break the app
      // Don't log to avoid console spam
    }
    
    return () => {
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
        } catch (err) {
          // Ignore unsubscribe errors
        }
        subscriptionRef.current = null;
      }
    };
  }, [userId, refetch]);

  
  // Process and format the chat room data with defensive coding
  useEffect(() => {
    if (!userId) {
      setChatRooms([]);
      return;
    }

    const processChatRooms = async () => {
      try {
        const hostChats = hostData?.chatRoomsByHostID?.items;
        const guestChats = guestData?.chatRoomsByGuestID?.items;

        if (hostChats === undefined && guestChats === undefined) {
          return;
        }

        const resolvedHostChats = hostChats || [];
        const resolvedGuestChats = guestChats || [];

        // Merge and deduplicate
        const allChats = [...resolvedHostChats, ...resolvedGuestChats];
        const uniqueChatsMap = new Map();
        allChats.forEach(chat => {
          if (chat && chat.id) {
            uniqueChatsMap.set(chat.id, chat);
          }
        });
        const uniqueChats = Array.from(uniqueChatsMap.values());

        if (uniqueChats.length === 0) {
          setChatRooms([]);
          return;
        }
        
        // Process all chat rooms and convert to old format for backward compatibility
        const formattedRooms = uniqueChats.map((chatRoom) => {
          // Determine chat partner based on whether user is host or guest
          const isHost = chatRoom.hostID === userId;
          const chatPartner = isHost ? chatRoom.guest : chatRoom.host;
          const exactLinkedBookingId = getExactLinkedBookingId(chatRoom);
          const bookingRelationItems = Array.isArray(chatRoom.bookings?.items)
            ? chatRoom.bookings.items.filter(Boolean)
            : [];
          const exactRelationBooking = exactLinkedBookingId
            ? bookingRelationItems.find((item) => String(item?.id) === String(exactLinkedBookingId))
            : null;
          const linkedBookingIDs = Array.isArray(chatRoom.linkedBookingIDs) && chatRoom.linkedBookingIDs.length
            ? chatRoom.linkedBookingIDs
            : bookingRelationItems.map((item) => item.id).filter(Boolean);
          
          // Process chat partner's profile picture
          let processedChatPartner = null;
          if (chatPartner) {
            processedChatPartner = { ...chatPartner };
            if (chatPartner.profilePicture) {
              try {
                processedChatPartner.profilePicture = getCloudFrontImageUrl(chatPartner.profilePicture, {
                  width: 200,
                  height: 200,
                  fit: 'cover'
                });
              } catch (error) {
                console.error('Error processing chat partner profile picture:', error);
              }
            }
          }
          
          // Process studio images if they exist
          let processedStudio = chatRoom.studio ? { ...chatRoom.studio } : null;
          if (processedStudio) {
            // Process studio image (single)
            if (processedStudio.image && !processedStudio.image.startsWith('http')) {
              try {
                processedStudio.image = getCloudFrontImageUrl(processedStudio.image);
              } catch (error) {
                console.error('Error processing studio image:', error);
              }
            }
            
            // Process studio images array
            if (processedStudio.images && Array.isArray(processedStudio.images)) {
              processedStudio.images = processedStudio.images.map((img) => {
                if (!img || typeof img !== 'string') return '';
                if (img.startsWith('http')) return img;
                try {
                  return getCloudFrontImageUrl(img);
                } catch (error) {
                  console.error('Error processing studio image:', error);
                  return '';
                }
              });
            }
          }
          
          // Convert to old format for backward compatibility, including new V2 fields
          return {
            id: chatRoom.id,
            createdAt: chatRoom.createdAt,
            updatedAt: chatRoom.updatedAt,
            name: processedStudio?.StudioName || 'Chat',
            image: processedStudio?.image || null,
            lastMessageAt: chatRoom.lastMessageAt,
            studioID: chatRoom.studioID,
            studio: processedStudio,
            host: chatRoom.host || null,
            guest: chatRoom.guest || null,
            chatPartner: processedChatPartner,
            lastMessage: chatRoom.lastMessageContent ? {
              id: 'temp',
              content: chatRoom.lastMessageContent,
              createdAt: chatRoom.lastMessageAt,
              senderID: chatRoom.lastMessageSenderID,
              type: chatRoom.lastMessageType || null,
              isSystemMessage: chatRoom.lastMessageIsSystemMessage === true
            } : null,
            lastMessageContent: chatRoom.lastMessageContent || '',
            lastMessageSenderID: chatRoom.lastMessageSenderID || null,
            lastMessageType: chatRoom.lastMessageType || null,
            lastMessageIsSystemMessage: chatRoom.lastMessageIsSystemMessage === true,
            // Include unread counts for notification badge (legacy fields for backward compatibility)
            hostUnreadCount: chatRoom.hostUnreadCount || 0,
            guestUnreadCount: chatRoom.guestUnreadCount || 0,
            // Include new V2 fields
            unreadCounts: chatRoom.unreadCounts || [],
            chatType: chatRoom.chatType,
            threadKind: chatRoom.threadKind,
            linkedBookingID: exactLinkedBookingId,
            linkedBookingIDs,
            bookings: chatRoom.bookings,
            bookingStartDate: chatRoom.bookingStartDate || exactRelationBooking?.startDateTime,
            bookingEndDate: chatRoom.bookingEndDate || exactRelationBooking?.endDateTime,
            isBookingRelated: Boolean(exactLinkedBookingId),
            participantCount: chatRoom.participantCount,
            chatStatus: chatRoom.chatStatus,
            retentionPolicy: chatRoom.retentionPolicy,
            threadGroupID: chatRoom.threadGroupID,
            threadGroupName: chatRoom.threadGroupName,
            archivedByHost: chatRoom.archivedByHost,
            archivedByHostAt: chatRoom.archivedByHostAt,
            archivedByGuest: chatRoom.archivedByGuest,
            archivedByGuestAt: chatRoom.archivedByGuestAt,
            becameReadOnlyAt: chatRoom.becameReadOnlyAt,
            scheduledForDeletionAt: chatRoom.scheduledForDeletionAt,
            hostID: chatRoom.hostID,
            guestID: chatRoom.guestID,
            participantIDs: chatRoom.participantIDs
          };
        });
        
        // Filter out any undefined/null rooms as an extra safety measure
        const validFormattedRooms = formattedRooms.filter(Boolean);
        
        // Sort by latest conversation activity (newest first):
        // Prefer lastMessageAt so new chat messages and booking status/system
        // messages bubble a conversation to the top.
        validFormattedRooms.sort((a, b) => {
          const dateA = a.lastMessageAt
            ? new Date(a.lastMessageAt).getTime()
            : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
          const dateB = b.lastMessageAt
            ? new Date(b.lastMessageAt).getTime()
            : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
          return dateB - dateA;
        });
        
        setChatRooms(validFormattedRooms);
      } catch (err) {
        console.error('Error processing chat rooms data:', err);
        setChatRooms([]);
      }
    };
    
    processChatRooms();
  }, [hostData, guestData, userId]);

  // Setup subscription when component mounts
  // NOTE: This subscription is optional - if WebSocket fails, the app still works
  // The chat list will still function, just won't auto-refresh on new messages
  // Per-chat subscriptions in useMessagesSubscription are more important
  useEffect(() => {
    if (!userId) return; // Don't setup subscription if userId is not available
    
    // Try to setup subscription, but don't let errors break the app
    try {
      const unsubscribe = subscribeToNewMessages();
      return () => {
        try {
          unsubscribe();
        } catch (err) {
          // Ignore unsubscribe errors
        }
      };
    } catch (err) {
      // Fail silently - subscription is optional
      console.warn('[useChatRooms] Could not setup subscription (non-critical)');
      return () => {}; // Return empty cleanup function
    }
  }, [subscribeToNewMessages, userId]);

  // Refetch on window focus for system messages and chat list updates
  // System messages from automation Lambdas are written directly to DynamoDB
  // and don't trigger subscriptions, so we refetch when window gains focus
  useEffect(() => {
    if (!userId) return;
    
    const handleFocus = () => {
      refetch();
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetch();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, refetch]);
  
  // Calculate total unread count across all chat rooms using new unreadCounts array with fallback
  const totalUnreadCount = chatRooms.reduce((total, room) => {
    if (!userId) return total;
    
    // Use new unreadCounts array if available
    if (room.unreadCounts && Array.isArray(room.unreadCounts) && room.unreadCounts.length > 0) {
      const userUnreadEntry = room.unreadCounts.find(
        (entry) => entry && entry.userID === userId
      );
      if (userUnreadEntry && typeof userUnreadEntry.count === 'number') {
        return total + userUnreadEntry.count;
      }
    }
    
    // Fallback to legacy fields
    const isHost = room.hostID === userId;
    const unread = isHost ? (room.hostUnreadCount || 0) : (room.guestUnreadCount || 0);
    return total + unread;
  }, 0);

  return {
    chatRooms,
    loading,
    error,
    refetch,
    totalUnreadCount
  };
};
